import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    const verifyEmail = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/api/auth/verify-email?token=${token}`
        );
        
        setStatus('success');
        setMessage(res.data.message);
        
        // Store token and user data (if provided)
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
        
        // Redirect based on whether it's already verified or newly verified
        setTimeout(() => {
          if (res.data.alreadyVerified) {
            navigate('/login');
          } else {
            navigate('/');
            window.location.reload(); // Reload to update auth state
          }
        }, 3000);
        
      } catch (error) {
        const errorData = error.response?.data;
        setStatus('error');
        setMessage(
          errorData?.message || 
          'Verification failed. The link may be invalid or expired.'
        );
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendEmail = async () => {
    const email = prompt('Please enter your email address:');
    if (!email) return;

    setResendLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/api/auth/resend-verification`,
        { email }
      );
      alert(res.data.message);
    } catch (error) {
      alert(
        error.response?.data?.message || 
        'Failed to resend verification email'
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f7f1 0%, #e6f4e8 100%)',
      padding: '20px'
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          background: 'white',
          borderRadius: '24px',
          padding: '48px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          textAlign: 'center'
        }}
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 24px',
            background: status === 'success' 
              ? 'linear-gradient(135deg, #15803d 0%, #166534 100%)'
              : status === 'error'
              ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px'
          }}
        >
          {status === 'verifying' && '⏳'}
          {status === 'success' && '✓'}
          {status === 'error' && '✕'}
        </motion.div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 800,
          color: '#1e293b',
          marginBottom: '16px'
        }}>
          {status === 'verifying' && 'Verifying Email...'}
          {status === 'success' && 'Email Verified!'}
          {status === 'error' && 'Verification Failed'}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#64748b',
          lineHeight: 1.6,
          marginBottom: '32px'
        }}>
          {message || 'Please wait while we verify your email address...'}
        </p>

        {/* Actions */}
        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{
              padding: '16px',
              background: '#f0fdf4',
              borderRadius: '12px',
              border: '2px solid #bbf7d0',
              color: '#166534',
              fontSize: '14px',
              marginTop: '24px'
            }}
          >
            Redirecting you to the homepage...
          </motion.div>
        )}

        {status === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleResendEmail}
              disabled={resendLoading}
              style={{
                padding: '16px 32px',
                background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: resendLoading ? 'not-allowed' : 'pointer',
                opacity: resendLoading ? 0.6 : 1
              }}
            >
              {resendLoading ? 'Sending...' : 'Resend Verification Email'}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/login')}
              style={{
                padding: '16px 32px',
                background: 'transparent',
                color: '#15803d',
                border: '2px solid #15803d',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Back to Login
            </motion.button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default VerifyEmail;
